import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  display: 'inline-block',
  maxWidth: '80%',
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: isAgent ? '#FFE5E5' : '#E5F0FF',
  color: '#333',
  ...(isAgent
    ? { borderTopLeftRadius: 0 }
    : { borderTopRightRadius: 0, marginLeft: 'auto' }),
}));

const TimeStamp = styled(Typography)({
  fontSize: '0.7rem',
  color: 'rgba(0, 0, 0, 0.54)',
  marginTop: 2,
});

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isAgent',
})<{ isAgent: boolean }>(({ isAgent }) => ({
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
  alignItems: isAgent ? 'flex-start' : 'flex-end',
}));

const TypingIndicator = styled(motion.div)(() => ({
  display: 'flex',
  gap: 6,
  padding: '12px 16px',
  borderRadius: '12px',
  backgroundColor: '#FFE5E5',
  borderTopLeftRadius: 0,
}));

const TypingDot = styled(motion.div)(() => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#D2691E', // Jago Coffee color
}));

interface OnboardingConversationAreaProps {
  transcript: Array<{
    source: string;
    message: string;
    timestamp: Date;
  }>;
  isSpeaking: boolean;
  agentInfo?: {
    profile: {
      name: string;
      role: string;
    };
  };
  onSendMessage?: (message: string) => void;
}

export function OnboardingConversationArea({
  transcript,
  isSpeaking,
  agentInfo,
  onSendMessage,
}: OnboardingConversationAreaProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [messageInput, setMessageInput] = useState('');

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript, isSpeaking]);

  const handleSendMessage = () => {
    if (messageInput.trim() && onSendMessage) {
      onSendMessage(messageInput.trim());
      setMessageInput('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography variant="body2" sx={{ color: '#D2691E', fontWeight: 600 }}>
          {agentInfo?.profile.name || 'Jago Coffee Trainer'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {agentInfo?.profile.role || 'Training Supervisor'}
        </Typography>
      </Box>

      <Divider />

      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
        }}
      >
        {transcript.map((item, index) => (
          <MessageContainer key={index} isAgent={item.source === 'ai'}>
            <MessageBubble isAgent={item.source === 'ai'} elevation={0}>
              {item.message}
            </MessageBubble>
            <TimeStamp>
              {item.timestamp.toLocaleTimeString('id-ID', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </TimeStamp>
          </MessageContainer>
        ))}

        {isSpeaking && (
          <MessageContainer isAgent={true}>
            <TypingIndicator>
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
              />
              <TypingDot
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
              />
            </TypingIndicator>
          </MessageContainer>
        )}
      </Box>

      {/* Multimodal input - always visible */}
      <>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            gap: 1,
            bgcolor: 'white',
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder={
              isSpeaking
                ? '🚫 Tunggu Sari selesai berbicara...'
                : 'Ketik pesan Anda atau gunakan mikrofon...'
            }
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSpeaking}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: isSpeaking ? '#f5f5f5' : 'white',
                '&.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                },
              },
            }}
          />

          <IconButton
            onClick={handleSendMessage}
            disabled={!messageInput.trim() || isSpeaking}
            sx={{
              bgcolor: '#D2691E',
              color: 'white',
              '&:hover': {
                bgcolor: '#CD853F',
              },
              '&:disabled': {
                bgcolor: '#E0E0E0',
              },
            }}
          >
            ➤
          </IconButton>
        </Box>
      </>
    </Box>
  );
}
